'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Building2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { Branch } from '@/src/types/team';
import { assignBranchesToMember, getMemberBranches } from '@/lib/api/team';
import { toast } from 'sonner';

interface BranchAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userName: string | null;
  userType: 'owner' | 'admin' | 'staff';
  availableBranches: Branch[];
  onSuccess?: () => void;
}

export function BranchAssignmentDialog({
  isOpen,
  onClose,
  userId,
  userName,
  userType,
  availableBranches,
  onSuccess,
}: BranchAssignmentDialogProps) {
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingCurrent, setIsFetchingCurrent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch current branch assignments when dialog opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchCurrentBranches();
    } else {
      // Reset state when dialog closes
      setSelectedBranches([]);
      setNotes('');
      setError(null);
    }
  }, [isOpen, userId]);

  const fetchCurrentBranches = async () => {
    if (!userId) return;

    setIsFetchingCurrent(true);
    setError(null);

    try {
      const response = await getMemberBranches(userId);
      if (response.success && response.data.branches) {
        setSelectedBranches(response.data.branches.map((b: Branch) => b.id));
      }
    } catch (err) {
      console.error('Error fetching current branches:', err);
      setError('ไม่สามารถโหลดข้อมูลสาขาปัจจุบันได้');
    } finally {
      setIsFetchingCurrent(false);
    }
  };

  const handleBranchToggle = (branchId: string) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBranches.length === availableBranches.length) {
      setSelectedBranches([]);
    } else {
      setSelectedBranches(availableBranches.map((b) => b.id));
    }
  };

  const handleSubmit = async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await assignBranchesToMember(userId, {
        branchIds: selectedBranches,
        notes: notes.trim() || undefined,
      });

      if (response.success) {
        toast.success('กำหนดสาขาเรียบร้อยแล้ว', {
          description: `กำหนดสาขาให้ ${userName} แล้ว ${selectedBranches.length} สาขา`,
        });
        onSuccess?.();
        onClose();
      } else {
        setError(response.error || 'เกิดข้อผิดพลาดในการกำหนดสาขา');
      }
    } catch (err: any) {
      console.error('Error assigning branches:', err);
      setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการกำหนดสาขา');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCount = selectedBranches.length;
  const totalCount = availableBranches.length;

  // Owner cannot have branch assignments
  if (userType === 'owner') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader className="space-y-3 pb-2">
            <DialogTitle className="text-xl">กำหนดสาขา</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <AlertDescription className="text-sm text-blue-900">
                Owner มีสิทธิ์เข้าถึงทุกสาขาโดยอัตโนมัติ ไม่จำเป็นต้องกำหนดสาขา
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
              ปิด
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[85vh]">
        <DialogHeader className="space-y-3 pb-4">
          <DialogTitle className="text-xl">กำหนดสาขาให้สมาชิก</DialogTitle>
          {userName && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              <span>สมาชิก: <span className="font-medium text-foreground">{userName}</span></span>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-5 py-2">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isFetchingCurrent ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">กำลังโหลดข้อมูล...</span>
            </div>
          ) : (
            <>
              {/* Summary & Select All */}
              <div className="flex items-center justify-between px-1 pb-3 border-b">
                <Badge variant="secondary" className="px-3 py-1.5 text-sm">
                  เลือกแล้ว {selectedCount} จาก {totalCount} สาขา
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={availableBranches.length === 0}
                  className="h-8"
                >
                  {selectedCount === totalCount ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                </Button>
              </div>

              {/* Branch List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">รายชื่อสาขา</Label>
                <div className="max-h-[280px] overflow-y-auto space-y-2.5 pr-2 rounded-md border p-3 bg-muted/30">
                  {availableBranches.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">ไม่พบสาขา</p>
                    </div>
                  ) : (
                    availableBranches.map((branch) => (
                      <div
                        key={branch.id}
                        className="flex items-start gap-3 rounded-lg border bg-background p-4 hover:bg-accent/50 transition-colors cursor-pointer shadow-sm"
                        onClick={() => handleBranchToggle(branch.id)}
                      >
                        <Checkbox
                          id={`branch-${branch.id}`}
                          checked={selectedBranches.includes(branch.id)}
                          onCheckedChange={() => handleBranchToggle(branch.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <Label
                            htmlFor={`branch-${branch.id}`}
                            className="font-medium text-base cursor-pointer leading-none"
                          >
                            {branch.branch_name}
                          </Label>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {branch.branch_code && (
                              <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">
                                #{branch.branch_code}
                              </span>
                            )}
                            {branch.is_main_branch && (
                              <Badge variant="outline" className="text-xs h-5">
                                สาขาหลัก
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2.5 pt-2">
                <Label htmlFor="notes" className="text-sm font-medium">
                  หมายเหตุ (ไม่บังคับ)
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="เพิ่มหมายเหตุเกี่ยวกับการกำหนดสาขา..."
                  rows={3}
                  maxLength={500}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {notes.length}/500 ตัวอักษร
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLoading} className="min-w-[100px]">
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || isFetchingCurrent || selectedBranches.length === 0}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              'บันทึก'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
